import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Container,
  Divider,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { Link as RouterLink } from 'react-router-dom'
import { CommandBlock } from '../components'
import {
  backIconSx,
  backLinkSx,
  containerSx,
  dividerSx,
  inlineLinkSx,
  listStyle,
  sectionSx,
  sectionTitleSx,
  titleSx,
} from '../styles'

export const SecurityModelPage: FC = () => {
  return (
    <Container maxWidth="md" sx={containerSx}>
      <Link component={RouterLink} to="/docs" sx={backLinkSx}>
        <ArrowBackIcon sx={backIconSx} />
        Back to Docs
      </Link>

      <Typography variant="h5" sx={titleSx}>
        Security Model
      </Typography>

      <Typography variant="h6" sx={sectionTitleSx}>
        Components
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>
            <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
              <strong>Daemon</strong>
            </Link>{' '}
            — background service running on your machine. Encrypts Claude&apos;s output, decrypts
            user input.
          </li>
          <li>
            <strong>Backend</strong> — relay server. Stores and forwards ciphertext. Has no keys,
            cannot decrypt.
          </li>
          <li>
            <strong>Dashboard</strong> — this application. Decrypts Claude&apos;s output, encrypts
            user input.
          </li>
        </ul>
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Key Ring
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>JSON file containing AES-256 keys — shared between dashboard and all daemons.</li>
          <li>Raw 256-bit keys from CSPRNG — no password derivation.</li>
          <li>
            Generated in the dashboard during{' '}
            <Link component={RouterLink} to="/docs/onboarding" sx={inlineLinkSx}>
              onboarding
            </Link>
            , exported as file, imported into each daemon.
          </li>
          <li>
            If you run multiple daemons across machines, every daemon and the dashboard must use the
            same key ring.
          </li>
        </ul>
      </Typography>
      <Table size="small" sx={tableSx}>
        <TableBody>
          {CRYPTO_PARAMS.map(({ label, value }) => (
            <TableRow key={label}>
              <TableCell sx={tableLabelCellSx}>{label}</TableCell>
              <TableCell sx={tableValueCellSx}>{value}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Encryption Scope
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>
            Everything is encrypted by default — including file paths, content, and tool arguments.
          </li>
          <li>Only routing metadata stays in plaintext so the backend can deliver messages.</li>
          <li>Unknown and future fields are automatically encrypted.</li>
        </ul>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={codeBlockLabelSx}>
        Encrypted event payload — tool use sent by the daemon:
      </Typography>
      <CommandBlock code={ENCRYPTED_PAYLOAD_EXAMPLE} language="json" />

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Key Rotation
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>Additive — new key version added, old keys preserved.</li>
          <li>Encryption uses the latest version. Decryption looks up by version.</li>
          <li>
            After rotation: re-export from dashboard, re-import to each{' '}
            <Link component={RouterLink} to="/docs/daemon-setup" sx={inlineLinkSx}>
              daemon
            </Link>
            .
          </li>
          <li>Daemon rejects version downgrades.</li>
        </ul>
      </Typography>

      <Divider sx={dividerSx} />

      <Typography variant="h6" sx={sectionTitleSx}>
        Transparency
      </Typography>
      <Typography variant="body1" color="text.secondary" component="div" sx={sectionSx}>
        <ul style={listStyle}>
          <li>The key ring is not password-protected — file access equals decryption access.</li>
          <li>
            If the key ring is lost: dashboard data becomes unreadable, but Claude Code&apos;s local
            state (transcripts, session files) remains on the originating machine.{' '}
            <Link
              href="https://code.claude.com/docs/en/data-usage"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Claude Code local state documentation (opens in new tab)"
            >
              Claude Code local state docs
            </Link>
          </li>
        </ul>
      </Typography>
    </Container>
  )
}

const ENCRYPTED_PAYLOAD_EXAMPLE = `{
  "raw_hook_payload": {
    "session_id": "e9783cf9-a3ad-48ba-8db6-f50652ddf441",
    "transcript_path": {
      "ciphertext": "Kvww0UxJG85mSYCK++5K6884y9XvTGcqznTk...",
      "iv": "iuMkzu6epy8duhNF"
    },
    "cwd": {
      "ciphertext": "4Qzt4myVDxcGlLxnNcz7Q1jTkNEfusD4qDFu...",
      "iv": "zeIN8wfb8xiM7iAM"
    },
    "permission_mode": "acceptEdits",
    "hook_event_name": "PostToolUse",
    "tool_name": "Bash",
    "tool_input": {
      "ciphertext": "nrK2fnbnPxoq5eYNedhuc9F4zUgZToTSAilF...",
      "iv": "DnEp7fE1XiZKfePd"
    },
    "tool_response": {
      "ciphertext": "Z+g9m5FEHbbJNExJqsTv0JDfHO6cx+qfHQ/t...",
      "iv": "C8Lqr9t67mhvR83M"
    },
    "tool_use_id": "toolu_01RtekksLn8jk1jTaYEou5uK"
  },
  "event_metadata": {
    "user_prompt_id": "ef371af2-768c-4a1e-97d4-6f324d0639ec",
    "event_id": "09c6a2b3-2629-4a83-8545-127fa14fa4f9",
    "timestamp": "2026-02-10T06:36:55.991945+00:00"
  },
  "session_metadata": {
    "session_id": "e9783cf9-a3ad-48ba-8db6-f50652ddf441",
    "transcript_path": {
      "ciphertext": "jn7Ni2d1/CrCDMwiVPSD9U3DAyGonuVTFL34...",
      "iv": "J/QKOFejPxc9MctO"
    },
    "work_directory": {
      "ciphertext": "Z3MUrCFKI/Kw+rzV6fPjUmAtZxEhgzEjiid2...",
      "iv": "QnOJO2fKyc7k49DI"
    },
    "agent_mode": "AUTO_ACCEPT_EDITS_MODE",
    "dangerously_skip_permissions": false
  },
  "host_details": {
    "host_name": "MacBook Pro",
    "host_id": "0179bc06-f845-4cda-9532-dfcfceefde1f"
  },
  "telemetry": {
    "tmux_session": {
      "sessionId": "$12",
      "sessionName": "daemon-encryption",
      "paneId": "%12"
    },
    "persist": true
  },
  "encryption": {
    "keyVersion": 3
  }
}`

const CRYPTO_PARAMS: readonly { label: string; value: string }[] = [
  { label: 'Algorithm', value: 'AES-256-GCM' },
  { label: 'Key', value: '256 bits, CSPRNG' },
  { label: 'IV', value: '96 bits, random per field' },
  { label: 'Auth tag', value: '128 bits' },
  { label: 'Wire format', value: '{ ciphertext, iv } base64-encoded' },
]

const tableSx: SxProps<Theme> = {
  mt: 2,
  mb: 1,
  '& .MuiTableCell-root': {
    borderBottom: 'none',
    py: 0.75,
    px: 1,
  },
}

const tableLabelCellSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontWeight: 500,
  width: 120,
  whiteSpace: 'nowrap',
}

const tableValueCellSx: SxProps<Theme> = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
}

const codeBlockLabelSx: SxProps<Theme> = {
  mt: 2,
  mb: 0,
  fontStyle: 'italic',
}
